import React, { useEffect, useState } from "react";
import api from "../utils/api";
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Invoices.css';

interface Invoice {
  id: number;
  bookingId: number | null;
  tboId?: number | null;
  amount: number;
  currency: string;
  createdAt: string;
  file?: string | null;
  status?: string;
  type?: string; // 'flight', 'hotel', 'payment'
  customer_name?: string;
  customer_email?: string;
}

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all'); // 'all', 'flight', 'hotel', 'payment'
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<string>('all'); // 'all', 'today', 'week', 'month'
  const [sortBy, setSortBy] = useState('created');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [invoices, searchTerm, typeFilter, statusFilter, dateRange, sortBy]);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.getInvoicesFromServer(1, 500);
      const list = (resp.invoices || []).map((i: any, idx: number) => ({
        id: i.id ?? `tmp-${idx}`,
        bookingId: i.bookingId ?? i.raw?.order_id ?? null,
        tboId: i.raw?.tbo_booking_id ?? null,
        amount: Number(i.amount ?? 0),
        currency: i.currency || 'INR',
        createdAt: i.createdAt ?? new Date().toISOString(),
        file: i.raw?.invoice_file || null,
        status: i.status || 'generated',
        type: i.type || 'payment',
        customer_name: i.customer_name,
        customer_email: i.customer_email,
      }));
      setInvoices(list);
      setFilteredInvoices(list);
    } catch (e: any) {
      console.warn("Invoices fetch failed", e);
      const status = e?.response?.status;
      if (status === 401) setError('Not authenticated — please login as admin');
      else if (status === 403) setError('Access denied — your account is not an admin');
      else setError(e?.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let filtered = [...invoices];

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.id.toString().includes(term) ||
        inv.bookingId?.toString().includes(term) ||
        inv.tboId?.toString().includes(term) ||
        inv.currency.toLowerCase().includes(term) ||
        inv.customer_name?.toLowerCase().includes(term) ||
        inv.customer_email?.toLowerCase().includes(term)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(inv => inv.type === typeFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday as start
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      filtered = filtered.filter(inv => {
        const invDate = new Date(inv.createdAt);
        if (dateRange === 'today') {
          return invDate >= today;
        } else if (dateRange === 'week') {
          return invDate >= startOfWeek;
        } else if (dateRange === 'month') {
          return invDate >= startOfMonth;
        }
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'amount':
          return b.amount - a.amount;
        case 'id':
          return (typeof b.id === 'number' ? b.id : 0) - (typeof a.id === 'number' ? a.id : 0);
        default:
          return 0;
      }
    });

    setFilteredInvoices(filtered);
  };

  const handleDownload = async (inv: Invoice) => {
    try {
      const downloadType = inv.type === 'flight' ? 'flight' : inv.type === 'hotel' ? 'hotel' : 'payment';
      const { url, headers } = await api.downloadInvoice(downloadType, inv.bookingId || inv.id);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const link = document.createElement('a');
      const fileName = inv.file || `invoice_${inv.id}.pdf`;
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.warn('Download failed', err);
      alert('Failed to download invoice file');
    }
  };

  const handleDownloadAll = () => {
    filteredInvoices.forEach((inv, index) => {
      setTimeout(() => handleDownload(inv), index * 500); // stagger downloads to avoid browser blocking
    });
  };

  const handlePreview = (inv: Invoice) => {
    setSelectedInvoice(inv);
    setPreviewOpen(true);
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'generated':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'overdue':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case 'flight': return '✈️';
      case 'hotel': return '🏨';
      default: return '📄';
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'flight': return '#f43b47';
      case 'hotel': return '#10b981';
      default: return '#6b7280';
    }
  };
  const handleExcelExport = () => {
    const rows = filteredInvoices.map((inv) => ({
      'Invoice ID': inv.id,
      'Booking ID': inv.bookingId || '',
      'TBO ID': inv.tboId || '',
      'Type': inv.type || '',
      'Customer Name': inv.customer_name || '',
      'Customer Email': inv.customer_email || '',
      'Amount': inv.amount,
      'Currency': inv.currency || 'INR',
      'Status': inv.status || '',
      'Created At': inv.createdAt ? new Date(inv.createdAt).toLocaleString() : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    XLSX.writeFile(wb, 'invoices.xlsx');
  };

  const handleTallyExport = async () => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (typeFilter !== 'all') params.append('type', typeFilter);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    
    // Convert date range to from_date/to_date if needed
    if (dateRange !== 'all') {
      const now = new Date();
      let fromDate = null;
      if (dateRange === 'today') {
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (dateRange === 'week') {
        fromDate = new Date(now);
        fromDate.setDate(now.getDate() - now.getDay());
      } else if (dateRange === 'month') {
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      if (fromDate) {
        params.append('from_date', fromDate.toISOString().split('T')[0]);
        // to_date is today
        params.append('to_date', now.toISOString().split('T')[0]);
      }
    }
    
    const response = await api.exportInvoicesTally(params);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'invoices_tally.xml');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    if (err.response?.status === 404) alert('No invoices to export');
    else alert('Export failed. Please try again.');
    console.error(err);
  }
};

  // Statistics by type
  const stats = {
    total: filteredInvoices.length,
    totalAmount: filteredInvoices.reduce((sum, i) => sum + i.amount, 0),
    flightCount: filteredInvoices.filter(i => i.type === 'flight').length,
    hotelCount: filteredInvoices.filter(i => i.type === 'hotel').length,
    otherCount: filteredInvoices.filter(i => i.type !== 'flight' && i.type !== 'hotel').length,
    paidCount: filteredInvoices.filter(i => i.status === 'paid').length,
    pendingCount: filteredInvoices.filter(i => i.status === 'pending').length,
  };

  return (
    <div className="invoices-page">
      <div className="page-header">
        <div>
          <h1>Invoices</h1>
          <p>Manage and track all invoices</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
      <button className="btn-primary" onClick={handleTallyExport}>
        📥 Download for Tally
      </button>
      <button className="btn-primary" onClick={handleExcelExport}>
        📊 Download Excel
      </button>
      {/* <button className="btn-primary">
        <span>➕</span> Create Invoice
      </button> */}
  </div>
      </div>

      {/* Type Tabs */}
      <div className="type-tabs">
        <button
          className={`type-tab ${typeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          All Invoices
        </button>
        <button
          className={`type-tab ${typeFilter === 'flight' ? 'active' : ''}`}
          onClick={() => setTypeFilter('flight')}
        >
          ✈️ Flight Invoices
        </button>
        <button
          className={`type-tab ${typeFilter === 'hotel' ? 'active' : ''}`}
          onClick={() => setTypeFilter('hotel')}
        >
          🏨 Hotel Invoices
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by ID, booking ID, customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="generated">Generated</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
          </select>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created">Newest</option>
            <option value="amount">Amount</option>
            <option value="id">Invoice ID</option>
          </select>
        </div>
      </div>

      <div className="stats-mini">
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total</span>
          <span className="stat-mini-value">{stats.total}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total Amount</span>
          <span className="stat-mini-value">₹{stats.totalAmount.toLocaleString()}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Flight</span>
          <span className="stat-mini-value">{stats.flightCount}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Hotel</span>
          <span className="stat-mini-value">{stats.hotelCount}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Paid</span>
          <span className="stat-mini-value">{stats.paidCount}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Pending</span>
          <span className="stat-mini-value">{stats.pendingCount}</span>
        </div>
        {filteredInvoices.length > 0 && (
          <button className="btn-outline download-all" onClick={handleDownloadAll} title="Download all displayed invoices (may open multiple tabs)">
            📥 Download All
          </button>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading invoices...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadInvoices} className="btn-outline">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="invoices-grid">
          {filteredInvoices.map((inv) => (
            <div key={inv.id} className="invoice-card" style={{ borderTop: `3px solid ${getTypeColor(inv.type)}` }}>
              <div className="card-header">
                <div className="invoice-id">
                  <span className="type-icon">{getTypeIcon(inv.type)}</span>
                  Invoice #{inv.id}
                </div>
                <span className={`status-badge ${getStatusClass(inv.status || '')}`}>
                  {inv.status || 'Generated'}
                </span>
              </div>
              <div className="card-body">
                <div className="invoice-details">
                  <div className="detail-row">
                    <span className="detail-label">Booking ID:</span>
                    <span className="detail-value">#{(inv.bookingId ?? " TEST-ID")}</span>
                  </div>
                  {inv.tboId && (
                    <div className="detail-row">
                      <span className="detail-label">TBO ID:</span>
                      <span className="detail-value">#{inv.tboId}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Date:</span>
                    <span className="detail-value">
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {inv.customer_name && (
                    <div className="detail-row">
                      <span className="detail-label">Customer:</span>
                      <span className="detail-value">{inv.customer_name}</span>
                    </div>
                  )}
                  <div className="detail-row amount-row">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value amount">
                      {inv.currency} {inv.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <button className="btn-view" onClick={() => handlePreview(inv)}>Preview</button>
                <div className="action-buttons">
                  <button className="btn-icon" onClick={() => handleDownload(inv)} title="Download">📥</button>
                  <button className="btn-icon" title="Email">📧</button>
                </div>
              </div>
            </div>
          ))}

          {filteredInvoices.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📄</span>
              <h3>No invoices found</h3>
              <p>Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewOpen && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="modal-content invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invoice #{selectedInvoice.id}</h3>
              <button className="close-btn" onClick={() => setPreviewOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="invoice-preview">
                <div className="preview-row">
                  <strong>Booking ID:</strong> #{selectedInvoice.bookingId}
                </div>
                {selectedInvoice.tboId && (
                  <div className="preview-row">
                    <strong>TBO ID:</strong> #{selectedInvoice.tboId}
                  </div>
                )}
                <div className="preview-row">
                  <strong>Date:</strong> {new Date(selectedInvoice.createdAt).toLocaleString()}
                </div>
                {selectedInvoice.customer_name && (
                  <div className="preview-row">
                    <strong>Customer:</strong> {selectedInvoice.customer_name}
                  </div>
                )}
                <div className="preview-row">
                  <strong>Amount:</strong> {selectedInvoice.currency} {selectedInvoice.amount.toLocaleString()}
                </div>
                <div className="preview-row">
                  <strong>Status:</strong> 
                  <span className={`status-badge ${getStatusClass(selectedInvoice.status || '')}`}>
                    {selectedInvoice.status || 'Generated'}
                  </span>
                </div>
                <hr />
                <div className="preview-actions">
                  <button className="btn-primary" onClick={() => handleDownload(selectedInvoice)}>
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;